<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_MEMO')"
            :label="$t('Add')"
            size="md"
            class="mt-0"
            @click="goToAddAccountMemo"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="accountMemoList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_MEMO')"
              @click="onSelectMemoItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectMemoItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_MEMO' : 'ACTIVATE_MEMO')"
              @click="accountMemoStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- memo modal -->
    <b-modal
      id="memo-modal-lg"
      ref="my-memo-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @cancel="resetModal"
    >
      <b-form @submit.prevent="handleSubmit">
        <b-row class="match-height">
          <b-col md="6">
            <SelectField
              id="action-type"
              v-model="accountMemoFormData.actionType"
              :label="$t('Action Type')"
              :reduce="option => option.value"
              :options="memoAccountActionTypesOptions"
              item="label"
              :placeholder="$t('Select Action Type')"
              readonly
            />
          </b-col>
          <b-col md="6">
            <SelectField
              id="reasons"
              v-model="accountMemoFormData.details"
              :label="$t('Reason')"
              :reduce="option => option.value"
              :options="memoAccountReasonsOptions"
              item="label"
              :placeholder="$t('Select Reason')"
              readonly
            />
          </b-col>
          <b-col md="12">
            <TextAreaField
              id="comments"
              v-model="accountMemoFormData.comments"
              :label="$t('Comments')"
              :placeholder="$t('Enter comments')"
              rows="1"
              :readonly="isReadonly"
            />
          </b-col>
        </b-row>
        <FullScreenSpinner :show="showSpinner" />
      </b-form>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- memo modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchAccountMemo"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <b-col md="6">
          <InputField
            id="initiated-by"
            v-model="filter.initiatedBy"
            :label="$t('Initiated By')"
            :placeholder="$t('Enter Initiated By')"
            type="email"
          />
        </b-col>

        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option => option.value"
            :options="memoStatus"
            item="label"
            :placeholder="$t('Select Status')"
          />
        </b-col>

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BForm, BBadge,
  BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { memoAccountActionTypes, memoAccountReasons } from '@/helpers/data'
import getStatusColor from '@/helpers/getStatusColor'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import { accountPermissions } from '@/helpers/permissions'
import Button from '../../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BForm,
    BRow,
    BCol,
    Button,
    BBadge,
    BDropdown,
    BDropdownItem,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    TextAreaField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      getStatusColor,
      modalTitle: '',
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'actionType', sortable: true, label: 'Action Type' },
        { key: 'details', sortable: true, label: 'Reason' },
        { key: 'comments', sortable: true, label: 'Comments' },
        { key: 'initiatedBy', sortable: true, label: 'Initiated By' },
        { key: 'lastModifiedBy', sortable: true, label: 'Last Modified By' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      accountMemoList: [],
      accountMemoFormData: {
        actionType: '',
        details: '',
        comments: '',
        id: '',
      },

      filter: {
        accountNumber: null,
        status: null,
        initiatedBy: '',
      },
      memoStatus: [
        { label: this.$t('Active'), value: 'Active' },
        { label: this.$t('InActive'), value: 'InActive' },
      ],
      showSpinner: false,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    memoAccountActionTypesOptions() {
      return memoAccountActionTypes.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    memoAccountReasonsOptions() {
      return memoAccountReasons.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchAccountMemo()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchAccountMemo()
    },
  },
  mounted() {
    this.fetchAccountMemo()
  },
  methods: {
    ...mapActions({
      getAccountMemo: 'account/getAccountMemoAction',
      updateMemoStatus: 'account/updateStatus',
      accountMemoUpdate: 'account/accountMemoUpdateAction',
    }),
    goToAddAccountMemo() {
      this.$router.push('add-memo')
    },
    async fetchAccountMemo() {
      try {
        this.isBusy = true
        const response = await this.getAccountMemo({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: 'VIEW_MEMO',
        })
        const { data } = response
        if (data.data !== null) {
          this.isBusy = false
          this.accountMemoList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async handleSubmit() {
      try {
        this.showSpinner = true
        this.isLoading = true
        this.isDisabled = true

        const { id, comments } = this.accountMemoFormData
        const formData = {
          id, comments,
        }
        const response = await this.accountMemoUpdate({ formData, permission: accountPermissions.MODIFY_MEMO })

        const { status, data } = response
        if (status === 200 && data) {
          this.isLoading = false
          this.isDisabled = false
          this.showSpinner = false
          this.fetchAccountMemo()
          this.alertSuccess({ text: this.$t('Your request to Modify Memo was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
        this.showSpinner = false
      }
    },
    onSelectMemoItem(items, activity, val) {
      try {
        this.accountMemoFormData = { ...items }
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Memo') : this.$t('View Memo')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'memo-modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async accountMemoStatus(items) {
      const { id, accountNumber } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_MEMO : accountPermissions.ACTIVATE_MEMO

      const updateStatus = async () => {
        const requestText = this.$t('Your request to')
        const translatedSuccess = this.$t('Memo was successful')
        const translatedFailed = this.$t('Memo failed')
        const translatedReason = this.$t('Reason')

        try {
          this.showSpinner = true
          const statusValues = {
            id,
            referenceNumber: accountNumber,
          }
          const response = await this.updateMemoStatus({
            statusValues, actionType, routeName: 'Memo', permission,
          })
          if (response.status === 200) {
            this.fetchAccountMemo()
            this.alertSuccess({ text: `${requestText} ${action} ${translatedSuccess}` })
            this.showSpinner = false
          }
        } catch (error) {
          error.response.data.errors.forEach(responseError => {
            this.alertError({ text: `${requestText} ${action} ${translatedFailed}. ${translatedReason}: ${responseError.errorMessage}` })
          })
        } finally {
          this.showSpinner = false
        }
      }
      // Change from using $t directly to this.$t
      const translatedQuestion = this.$t('Are you sure you want to')
      const translatedText = this.$t('this Memo')
      this.confirm(updateStatus, { title: '', text: `${translatedQuestion} ${action} ${translatedText}?` })
    },

    resetModal() {
      this.isReadonly = false
      this.isLoading = false
      this.modalTitle = 'Create Memo'
      this.buttonLabel = 'Save'
    },
    resetFilters() {
      this.filter = {
        ...null,
      }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchAccountMemo()
    },
  },
}
</script>
